import React, { useCallback, useRef, useState, useEffect } from 'react'
import { TextField, TextFieldProps } from '@material-ui/core'

type CustomTextFieldProps = TextFieldProps & {
  debounceTime?: number
  sanitizeValue?: (value: string) => string
  maxLength?: number
}

const buildHelperText = (
  maxLength: number | undefined,
  helperTextProp: TextFieldProps['helperText'],
  error?: boolean,
): TextFieldProps['helperText'] => {
  const maxHint = maxLength != null ? `Máximo ${maxLength} caracteres` : ''

  if (helperTextProp == null || helperTextProp === false) {
    return maxHint || undefined
  }

  const raw =
    typeof helperTextProp === 'string' || typeof helperTextProp === 'number'
      ? String(helperTextProp).trim()
      : ''
  if (!maxHint) {
    return helperTextProp
  }
  if (!raw) {
    return maxHint
  }
  const looksLikeSameMax = raw.includes('Máximo') && raw.includes(String(maxLength))
  if (looksLikeSameMax && error) {
    return raw
  }
  if (looksLikeSameMax && !error) {
    return maxHint
  }
  return `${raw} · ${maxHint}`
}

const CustomTextField: React.FC<CustomTextFieldProps> = ({
  debounceTime,
  sanitizeValue: sanitizeValueProp,
  maxLength,
  onChange,
  value,
  inputProps: inputPropsProp,
  helperText: helperTextProp,
  error,
  InputLabelProps,
  FormHelperTextProps,
  ...restProps
}) => {
  const timeoutRef = useRef<NodeJS.Timeout>()
  const [localValue, setLocalValue] = useState(value)

  useEffect(() => {
    setLocalValue(value)
  }, [value])

  const applyMaxAndSanitize = useCallback(
    (raw: string) => {
      const afterSanitize = sanitizeValueProp ? sanitizeValueProp(raw) : raw
      return maxLength != null ? afterSanitize.slice(0, maxLength) : afterSanitize
    },
    [maxLength, sanitizeValueProp],
  )

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.value
      const sanitizedValue = applyMaxAndSanitize(newValue)
      setLocalValue(sanitizedValue)

      const eventForCallback = {
        ...event,
        target: { ...event.target, value: sanitizedValue },
      } as React.ChangeEvent<HTMLInputElement>

      if (debounceTime && onChange) {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current)
        }
        timeoutRef.current = setTimeout(() => {
          onChange(eventForCallback)
        }, debounceTime)
      } else if (onChange) {
        onChange(eventForCallback)
      }
    },
    [applyMaxAndSanitize, debounceTime, onChange],
  )

  const mergedInputProps = {
    style: {
      fontFamily: 'Montserrat',
      fontWeight: 400,
      fontSize: 14,
      paddingLeft: 10,
      paddingBottom: 6,
      ...inputPropsProp?.style,
    },
    ...inputPropsProp,
    ...(maxLength != null ? { maxLength } : {}),
  }

  const defaultInputLabelStyle = {
    fontFamily: 'Montserrat',
    fontWeight: 400,
    fontSize: 14,
    paddingLeft: 10,
    paddingBottom: 8,
    lineHeight: '12px',
  }

  const defaultHelperTextStyle = {
    fontFamily: 'Montserrat',
    fontSize: 10,
    marginTop: 4,
  }

  const mergedHelperText = buildHelperText(maxLength, helperTextProp, error)

  return (
    <TextField
      {...restProps}
      inputProps={mergedInputProps}
      InputLabelProps={{
        style: { ...defaultInputLabelStyle, ...InputLabelProps?.style },
        ...InputLabelProps,
      }}
      FormHelperTextProps={{
        style: { ...defaultHelperTextStyle, ...FormHelperTextProps?.style },
        ...FormHelperTextProps,
      }}
      error={error}
      helperText={mergedHelperText}
      onChange={handleChange}
      value={localValue == null ? '' : localValue}
    />
  )
}

CustomTextField.defaultProps = {
  debounceTime: undefined,
  sanitizeValue: undefined,
  maxLength: undefined,
}

export default CustomTextField
